import { useCallback, useMemo } from 'react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../../utils/apiClient'

const buildParams = filters => {
	const params = {}

	if (filters.originLat != null && filters.originLng != null) {
		params.originLat = filters.originLat
		params.originLng = filters.originLng
		if (filters.originDh) params.originRadiusMiles = filters.originDh
	}

	if (filters.destinationLat != null && filters.destinationLng != null) {
		params.destinationLat = filters.destinationLat
		params.destinationLng = filters.destinationLng
		if (filters.destinationDh) params.destinationRadiusMiles = filters.destinationDh
	}

	if (filters.equipment && filters.equipment !== 'all') {
		params.equipment = filters.equipment
	}

	if (filters.source && filters.source !== 'all') {
		params.source = filters.source
	}

	if (filters.pickDateFrom) {
		const start = new Date(`${filters.pickDateFrom}T00:00:00`)
		params.pickupDateFrom = start.toISOString()
	}
	if (filters.pickDateTo) {
		const end = new Date(`${filters.pickDateTo}T23:59:59.999`)
		params.pickupDateTo = end.toISOString()
	}

	// Max weight in lbs. 0 / empty / null → no filter applied.
	const maxW = Number(filters.maxWeight)
	if (Number.isFinite(maxW) && maxW > 0) {
		params.maxWeight = maxW
	}

	const minD = Number(filters.minDistance)
	if (Number.isFinite(minD) && minD > 0) {
		params.minDistance = minD
	}

	const maxD = Number(filters.maxDistance)
	if (Number.isFinite(maxD) && maxD > 0) {
		params.maxDistance = maxD
	}

	const lastSeenH = Number(filters.lastSeenHours)
	if (Number.isFinite(lastSeenH) && lastSeenH > 0) {
		params.lastSyncedWithinHours = lastSeenH
	}

	// Map the dropdown choice to the backend's generic sortBy/sortOrder pair.
	const SORT_PARAMS = {
		lastSeen: { sortBy: 'lastSyncedAt', sortOrder: 'desc' },
		pickup: { sortBy: 'pickupDate', sortOrder: 'asc' },
		pay: { sortBy: 'price', sortOrder: 'desc' },
	}
	const sort = SORT_PARAMS[filters.sort] || SORT_PARAMS.pickup
	params.sortBy = sort.sortBy
	params.sortOrder = sort.sortOrder

	params.pageSize = 100

	if (filters.showArchive) {
		params.showArchive = true
	}

	return params
}

export default function useLoads(filters) {
	const queryClient = useQueryClient()
	const params = useMemo(() => buildParams(filters), [filters])

	const query = useQuery({
		queryKey: ['loads', params],
		placeholderData: keepPreviousData,
		staleTime: 0, // Always refetch; no caching
		gcTime: 0, // Clear cache immediately
		queryFn: async ({ signal }) => {
			const res = await api.get('/api/loads', { params, signal })
			return {
				data: res.data?.data || [],
				hasMore: Boolean(res.data?.meta?.hasMore),
				reason: res.data?.meta?.reason ?? null,
			}
		},
	})

	const refetch = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['loads'] })
	}, [queryClient])

	return {
		loads: query.data?.data ?? [],
		hasMore: Boolean(query.data?.hasMore),
		loading: query.isFetching,
		isPlaceholderData: query.isPlaceholderData,
		error: query.error || null,
		reason: query.data?.reason ?? null,
		refetch,
	}
}
